import axios from '../../axios';
import { setLocalStorageWithExpiry } from '../../utils/localstorage';
import { safeLocalStorage } from '../storage/safeLocalStorage';
import { isVerboseLoggingEnabled } from '../debug/logging';
import { getRefreshToken } from '../../utils/spotify/login';

const REFRESH_TOKEN_TTL_MS = 365 * 24 * 60 * 60 * 1000;
const CODE_VERIFIER_KEY = 'spotify_code_verifier';

const SPOTIFY_SCOPES = [
  'user-read-private',
  'user-read-email',
  'user-top-read',
  'user-read-recently-played',
  'user-read-playback-state',
  'user-modify-playback-state',
  'user-read-currently-playing',
  'streaming',
] as const;

const isDevelopmentEnv =
  typeof process !== 'undefined'
    ? process.env.NODE_ENV !== 'production'
    : true;
const isDevelopment = isDevelopmentEnv && isVerboseLoggingEnabled();
const LOG_NAMESPACE = '[SpotifyTokenTrace]';

const maskToken = (token: string | null | undefined): string => {
  if (typeof token !== 'string' || token.length === 0) return 'null';
  return token;
};

const traceDebug = (message: string, details?: Record<string, unknown>) => {
  if (isDevelopment) {
    console.log(LOG_NAMESPACE, message, details ?? '');
  }
};

const traceWarn = (message: string, details?: Record<string, unknown>) => {
  console.warn(LOG_NAMESPACE, message, details ?? '');
};

const getClientId = () => {
  return process.env.REACT_APP_SPOTIFY_CLIENT_ID || '';
};

const getRedirectUri = () => {
  return process.env.REACT_APP_SPOTIFY_REDIRECT_URL || window.location.origin;
};

const base64UrlEncode = (buffer: ArrayBuffer): string => {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

const generateRandomString = (length: number): string => {
  const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
  const array = new Uint8Array(length);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    crypto.getRandomValues(array);
    return Array.from(array, (x) => charset[x % charset.length]).join('');
  }
  let result = '';
  for (let i = 0; i < length; i++) {
    result += charset.charAt(Math.floor(Math.random() * charset.length));
  }
  return result;
};

const createCodeChallenge = async (codeVerifier: string): Promise<string> => {
  const encoder = new TextEncoder();
  const data = encoder.encode(codeVerifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return base64UrlEncode(digest);
};

const getStoredToken = (...keys: string[]): string | null => {
  for (const key of keys) {
    const raw = safeLocalStorage.getItem(key);
    if (!raw) continue;
    try {
      const parsed = JSON.parse(raw) as { value?: unknown; expiry?: number };
      if (typeof parsed.expiry === 'number' && parsed.expiry <= Date.now()) {
        safeLocalStorage.removeItem(key);
        continue;
      }
      if ('value' in parsed) {
        const value = parsed.value;
        if (typeof value === 'string') return value;
        if (value != null) return String(value);
        continue;
      }
    } catch {
      return raw;
    }
  }
  return null;
};

const getStoredAccessToken = () => getStoredToken('access_token', 'spotify_access_token');

const storeAccessToken = (token: string, expiresInSeconds: number) => {
  const ttl = expiresInSeconds * 1000;
  traceDebug('storeAccessToken', { token: maskToken(token), ttlMs: ttl });
  setLocalStorageWithExpiry('access_token', token, ttl);
  setLocalStorageWithExpiry('spotify_access_token', token, ttl);
  axios.defaults.headers.common['Authorization'] = 'Bearer ' + token;
  if (typeof window !== 'undefined') {
    (window as any).__spotifyTokenTrace = token;
  }
};

const storeRefreshToken = (refreshToken: string) => {
  traceDebug('storeRefreshToken', { refreshToken: maskToken(refreshToken) });
  setLocalStorageWithExpiry('refresh_token', refreshToken, REFRESH_TOKEN_TTL_MS);
  setLocalStorageWithExpiry('spotify_refresh_token', refreshToken, REFRESH_TOKEN_TTL_MS);
};

const clearStoredTokens = () => {
  traceWarn('Clearing stored Spotify tokens');
  safeLocalStorage.removeItem('access_token');
  safeLocalStorage.removeItem('spotify_access_token');
  safeLocalStorage.removeItem('refresh_token');
  safeLocalStorage.removeItem('spotify_refresh_token');
  safeLocalStorage.removeItem('public_access_token');
};

const init = async (): Promise<void> => {
  const token = getStoredAccessToken();
  if (token) {
    axios.defaults.headers.common['Authorization'] = 'Bearer ' + token;
    return;
  }

  const refreshToken = getStoredToken('refresh_token', 'spotify_refresh_token');
  if (!refreshToken) {
    return;
  }

  try {
    const newToken = await refreshAccessToken();
    if (newToken) {
      axios.defaults.headers.common['Authorization'] = 'Bearer ' + newToken;
    }
  } catch (error) {
    clearStoredTokens();
    if (isDevelopment) {
      console.warn('Failed to refresh Spotify token during init', error);
    }
  }
};

const isAuthenticated = (): boolean => {
  return !!getStoredAccessToken() || !!getStoredToken('refresh_token', 'spotify_refresh_token');
};

const authenticate = async (): Promise<void> => {
  const clientId = getClientId();
  const redirectUri = getRedirectUri();

  if (!clientId) {
    throw new Error('Spotify client ID not configured. Please set REACT_APP_SPOTIFY_CLIENT_ID.');
  }

  const codeVerifier = generateRandomString(96);
  safeLocalStorage.setItem(CODE_VERIFIER_KEY, codeVerifier);
  const codeChallenge = await createCodeChallenge(codeVerifier);

  const authUrl = new URL('https://accounts.spotify.com/authorize');
  authUrl.searchParams.set('client_id', clientId);
  authUrl.searchParams.set('response_type', 'code');
  authUrl.searchParams.set('redirect_uri', redirectUri);
  authUrl.searchParams.set('scope', SPOTIFY_SCOPES.join(' '));
  authUrl.searchParams.set('code_challenge_method', 'S256');
  authUrl.searchParams.set('code_challenge', codeChallenge);

  window.location.href = authUrl.toString();
};

const handleCallback = async (code: string): Promise<void> => {
  const clientId = getClientId();
  const redirectUri = getRedirectUri();
  const codeVerifier = safeLocalStorage.getItem(CODE_VERIFIER_KEY);

  if (!clientId) {
    throw new Error('Spotify client ID not configured. Please set REACT_APP_SPOTIFY_CLIENT_ID.');
  }
  if (!codeVerifier) {
    throw new Error('Missing PKCE code verifier. Please restart Spotify authentication.');
  }

  try {
    const response = await fetch('https://accounts.spotify.com/api/token', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        grant_type: 'authorization_code',
        code,
        redirect_uri: redirectUri,
        client_id: clientId,
        code_verifier: codeVerifier,
      }),
    });

    const data = await response.json();

    if (!response.ok || !data.access_token) {
      const description =
        typeof data?.error_description === 'string'
          ? data.error_description
          : 'Failed to complete Spotify authentication. Please try again.';
      safeLocalStorage.removeItem(CODE_VERIFIER_KEY);
      throw new Error(description);
    }

    storeAccessToken(data.access_token, data.expires_in);
    if (data.refresh_token) {
      storeRefreshToken(data.refresh_token);
    }

    safeLocalStorage.removeItem(CODE_VERIFIER_KEY);
  } catch (error) {
    safeLocalStorage.removeItem(CODE_VERIFIER_KEY);
    throw error;
  }
};

const refreshAccessToken = async (): Promise<string> => {
  traceDebug('refreshAccessToken start - delegating to login.getRefreshToken');
  // Use the centralized refresh implementation from login.ts
  // This ensures coordinated refreshes and prevents duplicate requests
  const token = await getRefreshToken();
  if (!token) {
    traceWarn('refreshAccessToken failed - getRefreshToken returned null');
    throw new Error('Failed to refresh Spotify session.');
  }
  traceDebug('refreshAccessToken success', {
    token: maskToken(token)
  });
  return token;
};

const getValidAccessToken = async (): Promise<string> => {
  const existing = getStoredAccessToken();
  if (existing) {
    if (typeof window !== 'undefined') {
      (window as any).__spotifyTokenTrace = existing;
    }
    traceDebug('getValidAccessToken cache hit', { token: maskToken(existing) });
    return existing;
  }
  traceDebug('getValidAccessToken cache miss');
  try {
    const refreshed = await refreshAccessToken();
    if (typeof window !== 'undefined') {
      (window as any).__spotifyTokenTrace = refreshed;
    }
    traceDebug('getValidAccessToken obtained refreshed token', {
      token: maskToken(refreshed)
    });
    return refreshed;
  } catch (error) {
    traceWarn('getValidAccessToken failed to refresh', {
      error: error instanceof Error ? error.message : String(error)
    });
    throw error;
  }
};

const logout = (): void => {
  traceDebug('logout clearing auth state');
  clearStoredTokens();
  safeLocalStorage.removeItem(CODE_VERIFIER_KEY);
  delete axios.defaults.headers.common['Authorization'];
};

export const authService = {
  init,
  isAuthenticated,
  authenticate,
  handleCallback,
  refreshAccessToken,
  getValidAccessToken,
  logout,
};
